import { cn } from "@patient-watch/ui/utils";
import type { CalendarDay } from "../utils/calendarHelpers";
import type { DayStats } from "../utils/studyTrackerHelpers";

interface StudyTrackerDayProps {
    day: CalendarDay;
    dayStats: DayStats | null;
    onClick: () => void;
}

export function StudyTrackerDay({ day, dayStats, onClick }: StudyTrackerDayProps) {
    const hasData = dayStats !== null;
    const completionRate = dayStats && dayStats.total > 0 ? dayStats.completed / dayStats.total : 0;

    // Determine styling based on completion status
    let bgColor = "";
    let textColor = "";
    if (hasData) {
        if (completionRate === 1) {
            // 100% complete
            bgColor = "bg-green-50";
            textColor = "text-green-700";
        } else if (completionRate > 0) {
            // Partial completion
            bgColor = "bg-amber-50";
            textColor = "text-amber-700";
        } else {
            // 0% complete (no submissions)
            bgColor = "bg-red-50";
            textColor = "text-red-700";
        }
    }

    return (
        <button
            type="button"
            onClick={onClick}
            disabled={!day.isCurrentMonth || !hasData}
            className={cn(
                "relative flex flex-col justify-center items-center p-2 rounded-lg aspect-square transition-all",
                "hover:bg-slate-100 active:scale-95",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:ring-offset-2",
                day.isCurrentMonth ? "text-slate-900" : "text-slate-300",
                !day.isCurrentMonth && "cursor-not-allowed opacity-50",
                hasData && bgColor
            )}
        >
            <span className={cn("mb-1 font-semibold text-slate-800 text-sm", hasData && textColor)}>
                {day.dayNumber}
            </span>
            {hasData && dayStats && (
                <span className={cn("font-medium text-xs", textColor)}>
                    {dayStats.completed}/{dayStats.total}
                </span>
            )}
            {day.isToday && (
                <div className="top-1 right-1 absolute">
                    <div className="bg-indigo-500 rounded-full w-1.5 h-1.5" />
                </div>
            )}
        </button>
    );
}
